import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { WrapperContext } from './Wrapper';

export interface HeaderProps {
  title: string;
  openCtaLabel?: string;
  isSingleValidate?: boolean;
  forceDisplayCta?: boolean;
}

export const Header = (props: HeaderProps) => {
  const context = React.useContext(WrapperContext);

  if (!context) {
    throw new Error('Wrapper.Header must be used inside Wrapper');
  }

  const { title } = props;

  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Typography variant="h6">{title}</Typography>
      <HeaderCTAs {...props} />
    </Stack>
  );
};

interface HeaderCTAsProps extends Omit<HeaderProps, 'title'> {}

const HeaderCTAs = (props: HeaderCTAsProps) => {
  const context = React.useContext(WrapperContext);

  if (!context) {
    throw new Error('Wrapper.Header must be used inside Wrapper');
  }

  const {
    isOwner,
    isEditMode,
    managingType,
    isManaging,
    setIsManaging,
    isSaving,
    setIsSaving,
    isSaveDisabled,
  } = context;
  const { openCtaLabel = 'edit', isSingleValidate, forceDisplayCta } = props;
  const { t } = useTranslation('cta');
  const theme = useTheme();

  const handleManageClick = () => {
    setIsManaging(managingType);
  };

  const handleManageCancel = () => {
    setIsManaging(null);
  };

  const handleManageSave = () => {
    if (isSingleValidate) {
      setIsManaging(null);
    } else {
      setIsSaving(true);
    }
  };

  if (!isOwner || (!isEditMode && !forceDisplayCta)) {
    return null;
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {isManaging !== managingType ? (
        <Button
          size="small"
          variant={isManaging !== null ? 'contained' : 'outlined'}
          color="uncolored"
          onClick={handleManageClick}
          startIcon={openCtaLabel === 'add' ? <theme.icons.add /> : <theme.icons.edit />}
          disabled={isManaging !== null}
        >
          {t(openCtaLabel)}
        </Button>
      ) : (
        <>
          {!isSingleValidate && !isSaving && (
            <Button
              size="small"
              variant="outlined"
              color="uncolored"
              onClick={handleManageCancel}
              disabled={isSaving}
            >
              {t('cancel')}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            color="uncolored"
            onClick={handleManageSave}
            disabled={!isSingleValidate && isSaveDisabled}
            loading={isSaving}
          >
            {t(isSingleValidate ? 'close' : 'save')}
          </Button>
        </>
      )}
    </Stack>
  );
};
